import { HTMLProps } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { duplicateCampaign as duplicateCampaignApi } from 'api/campaigns';
import * as Dialog from 'components/dialog';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Campaign } from 'types/domain';

interface DuplicateCampaignModalProps extends Dialog.DialogProps {
    campaign: Campaign;
    onDuplicateSuccess?: (campaign: Campaign) => void;
    autoCloseAfterSuccess?: boolean;
}

const DuplicateCampaignModal = (props: DuplicateCampaignModalProps) => {
    const { campaign, children, onDuplicateSuccess, autoCloseAfterSuccess = true, ...rest } = props;
    const toast = useToast();
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();

    const { mutate: duplicateCampaign } = useMutation({
        mutationFn: () => duplicateCampaignApi(campaign.id),
        onSuccess: (campaign: Campaign) => {
            toast.success('Campaign duplicated!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.campaigns(accountId),
                type: 'all',
            });
            autoCloseAfterSuccess && forceRender();
            onDuplicateSuccess?.(campaign);
        },
        onError: () => {
            toast.error('Unable to duplicate campaign');
        },
    });

    return (
        <Dialog.Root key={key} {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Dialog.Header className={'flex flex-col gap-y-2'}>
                        <Dialog.Title>Duplicate Campaign</Dialog.Title>
                        <p className={'text-base text-gray-600'}>{campaign.title}</p>
                    </Dialog.Header>
                    <Dialog.Body className={'flex flex-col gap-y-4'}>
                        <div className={'flex flex-col gap-y-2'}>
                            <p className={'text-xl font-medium'}>What will be copied</p>
                            <ul className={'flex flex-col gap-y-1'}>
                                <CopiedListItem>
                                    Title, Subtitle, Goal, Images & Theme Color
                                </CopiedListItem>
                                <CopiedListItem>Story</CopiedListItem>
                                <CopiedListItem>Settings</CopiedListItem>
                                <CopiedListItem>Custom Fields</CopiedListItem>
                                <CopiedListItem>
                                    Event Details, Ticket Types, and & Discount Codes
                                </CopiedListItem>
                                <CopiedListItem>Auction Details & Items</CopiedListItem>
                            </ul>
                        </div>
                        <div className={'flex flex-col gap-y-2'}>
                            <p className={'text-xl font-medium'}>What won't be copied</p>
                            <ul className={'flex flex-col gap-y-1'}>
                                <NotCopiedListItem>Transactions</NotCopiedListItem>
                                <NotCopiedListItem>Teams & Team Members</NotCopiedListItem>
                            </ul>
                        </div>
                        <div className={'mt-4 flex justify-between'}>
                            <Dialog.Cancel asChild={true}>
                                <Button variant={'outline'} color={'neutral'}>
                                    Cancel
                                </Button>
                            </Dialog.Cancel>
                            <Button variant="solid" onClick={() => duplicateCampaign()}>
                                Duplicate
                            </Button>
                        </div>
                    </Dialog.Body>
                    <Dialog.Close />
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

interface CopiedListItemProps extends HTMLProps<'li'> {}

const CopiedListItem = (props: CopiedListItemProps) => (
    <li className={'flex items-center gap-x-2'}>
        <FontAwesomeIcon size={'xl'} className={'text-green-500'} icon={faCircleCheck} />
        <p className={'text-base'}>{props.children}</p>
    </li>
);

const NotCopiedListItem = (props: CopiedListItemProps) => (
    <li className={'flex items-center gap-x-2'}>
        <FontAwesomeIcon size={'xl'} className={'text-red-500'} icon={faCircleXmark} />
        <p className={'text-base'}>{props.children}</p>
    </li>
);

const DuplicateCampaignModalTrigger = Dialog.Trigger;

const Root = DuplicateCampaignModal;
const Trigger = DuplicateCampaignModalTrigger;

export {
    DuplicateCampaignModal,
    DuplicateCampaignModalTrigger,
    //
    Root,
    Trigger,
};
export type { DuplicateCampaignModalProps };
